import { inject, Injectable } from '@angular/core';
import { Resource, Service } from 'ngx-jsonapi';
import { lastValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';
import { VenuePhotoService } from '../venue-photo.service';

import {
  GiggerProfileDetail,
  MusicianProfileDetail,
  PublicProfileEventSummary,
  PublicProfileDetailBase,
} from './public-profile.models';

export interface ArtistDirectoryEntry {
  id: string;
  publicId: string;
  name: string;
  description: string;
  profilePhoto: string | null;
  latitude: number | null;
  longitude: number | null;
  styles: string[];
  memberCount: number | null;
  managementContact: string;
  website: string;
  eventCount: number;
  isFollowed: boolean;
  currentFollowId: string | null;
}

export interface FetchArtistDirectoryPageOptions {
  pageNumber: number;
  pageSize?: number;
  searchQuery: string;
  styleId: number | null;
  styleIds?: number[];
  requestScope?: string;
  distanceKm: number;
  userLatitude: number | null;
  userLongitude: number | null;
}

export interface ArtistDirectoryPage {
  giggers: ArtistDirectoryEntry[];
  hasMorePages: boolean;
}

export class User extends Resource {
  public override attributes = {
    public_id: '',
    display_name: '',
    bio: '',
    primary_role: 0,
    latitude: null as string | null,
    longitude: null as string | null,
    profile_photo: null as string | null,
    google_place_id: '',
    artist_styles: [] as string[],
    member_count: null as number | null,
    management_contact: '',
    website: '',
    event_count: 0,
    is_followed: false,
    current_follow_id: null as string | null,
    phone: '',
    follower_count: 0,
    likes_count: 0,
    is_liked: false,
    current_like_id: null as string | null,
    gallery_images: [] as Array<{ id: string; url: string; caption: string }>,
    external_links: [] as Array<{ id: string; platform: string; label: string; url: string }>,
    upcoming_events: [] as Array<{
      id: string;
      public_id: string;
      title: string;
      poster_url: string | null;
      fallback_image_url: string | null;
      fallback_google_place_id: string;
      date: string;
      time: string;
      likes_count: number;
      is_liked: boolean;
      current_like_id: string | null;
      is_past: boolean;
    }>,
    past_events: [] as Array<{
      id: string;
      public_id: string;
      title: string;
      poster_url: string | null;
      fallback_image_url: string | null;
      fallback_google_place_id: string;
      date: string;
      time: string;
      likes_count: number;
      is_liked: boolean;
      current_like_id: string | null;
      is_past: boolean;
    }>,
    frequent_collaborators: [] as Array<{
      id: string;
      public_id: string;
      name: string;
      profile_photo: string | null;
      google_place_id: string;
      event_count: number;
    }>,
    frequent_venues: [] as Array<{
      id: string;
      public_id: string;
      name: string;
      profile_photo: string | null;
      google_place_id: string;
      event_count: number;
    }>,
    musician_instruments: [] as Array<{
      instrument_id: number;
      instrument_name: string;
      skill_level: number;
      skill_level_label: string;
      years_experience: number | null;
      styles: string[];
    }>,
  };

  public override relationships = {};
}

@Injectable({
  providedIn: 'root',
})
export class UserService extends Service<User> {
  private readonly venuePhotos = inject(VenuePhotoService);
  private readonly directoryPageSize = 24;
  private readonly activeDirectoryRequests = new Map<string, Subject<void>>();

  public override resource = User;
  public override type = 'User';
  public override path = '/api/users';

  public async fetchArtistDirectoryPage(options: FetchArtistDirectoryPageOptions): Promise<ArtistDirectoryPage> {
    const request$ = this.fetchArtistDirectoryPage$(options);
    const requestScope = options.requestScope;
    if (!requestScope) {
      return lastValueFrom(request$);
    }

    this.cancelArtistDirectoryRequest(requestScope);
    const cancellation$ = new Subject<void>();
    this.activeDirectoryRequests.set(requestScope, cancellation$);

    try {
      return await lastValueFrom(request$.pipe(takeUntil(cancellation$)));
    } finally {
      if (this.activeDirectoryRequests.get(requestScope) === cancellation$) {
        this.activeDirectoryRequests.delete(requestScope);
      }
      cancellation$.complete();
    }
  }

  public cancelArtistDirectoryRequest(requestScope: string): void {
    const cancellation$ = this.activeDirectoryRequests.get(requestScope);
    if (!cancellation$) {
      return;
    }

    this.activeDirectoryRequests.delete(requestScope);
    cancellation$.next();
    cancellation$.complete();
  }

  public fetchArtistDirectoryPage$(options: FetchArtistDirectoryPageOptions): Observable<ArtistDirectoryPage> {
    return this.all({
      remotefilter: this.buildRemoteFilter(options),
      page: {
        number: options.pageNumber,
        size: options.pageSize ?? this.directoryPageSize,
      },
    }).pipe(
      map((page) => ({
        giggers: page.data.map((user) => this.toArtistDirectoryEntry(user)),
        hasMorePages: page.hasMorePages() === true,
      })),
    );
  }

  public async fetchArtistProfileDetail(artistId: string): Promise<GiggerProfileDetail> {
    const user = await lastValueFrom(this.get(artistId));
    const [base, frequentVenues] = await Promise.all([
      this.toPublicProfileDetailBase(user),
      Promise.all((user.attributes.frequent_venues ?? []).map(async (venue) => {
        const photo = await this.venuePhotos.resolve(venue.profile_photo, venue.google_place_id);
        return {
          id: venue.id,
          publicId: venue.public_id,
          name: venue.name,
          profilePhoto: photo?.url ?? null,
          eventCount: venue.event_count,
        };
      })),
    ]);
    return {
      ...base,
      kind: 'gigger',
      styles: user.attributes.artist_styles,
      memberCount: user.attributes.member_count,
      managementContact: user.attributes.management_contact,
      frequentVenues,
    };
  }

  public async fetchMusicianProfileDetail(musicianId: string): Promise<MusicianProfileDetail> {
    const user = await lastValueFrom(this.get(musicianId));

    return {
      ...await this.toPublicProfileDetailBase(user),
      kind: 'musician',
      instruments: (user.attributes.musician_instruments ?? []).map((instrument) => ({
        instrumentId: Number(instrument.instrument_id),
        instrumentName: instrument.instrument_name,
        skillLevel: Number(instrument.skill_level),
        skillLevelLabel: instrument.skill_level_label,
        yearsExperience: instrument.years_experience !== null
          ? Number(instrument.years_experience)
          : null,
        styles: instrument.styles ?? [],
      })),
    };
  }

  private buildRemoteFilter(options: FetchArtistDirectoryPageOptions): Record<string, string> {
    const filter: Record<string, string> = {
      primary_role: '3',
    };

    const trimmedQuery = options.searchQuery.trim();
    if (trimmedQuery) {
      filter['search'] = trimmedQuery;
    }

    if (options.styleIds?.length) {
      filter['artist_styles'] = options.styleIds.join(',');
    } else if (options.styleId !== null) {
      filter['artist_style'] = String(options.styleId);
    }

    if (
      options.distanceKm > 0
      && options.userLatitude !== null
      && options.userLongitude !== null
    ) {
      filter['user_latitude'] = String(options.userLatitude);
      filter['user_longitude'] = String(options.userLongitude);
      filter['distance_lte'] = String(options.distanceKm);
    }

    return filter;
  }

  private toArtistDirectoryEntry(user: User): ArtistDirectoryEntry {
    return {
      id: user.id,
      publicId: user.attributes.public_id,
      name: this.resolveDisplayName(user),
      description: user.attributes.bio,
      profilePhoto: user.attributes.profile_photo,
      latitude: user.attributes.latitude ? Number(user.attributes.latitude) : null,
      longitude: user.attributes.longitude ? Number(user.attributes.longitude) : null,
      styles: user.attributes.artist_styles,
      memberCount: user.attributes.member_count,
      managementContact: user.attributes.management_contact,
      website: user.attributes.website,
      eventCount: user.attributes.event_count,
      isFollowed: user.attributes.is_followed,
      currentFollowId: user.attributes.current_follow_id,
    };
  }

  private async toPublicProfileDetailBase(user: User): Promise<PublicProfileDetailBase> {
    const [profilePhoto, upcomingEvents, pastEvents] = await Promise.all([
      this.venuePhotos.resolve(user.attributes.profile_photo, user.attributes.google_place_id),
      Promise.all((user.attributes.upcoming_events ?? []).map((event) => this.toProfileEventSummary(event))),
      Promise.all((user.attributes.past_events ?? []).map((event) => this.toProfileEventSummary(event))),
    ]);
    return {
      id: user.id,
      publicId: user.attributes.public_id,
      name: this.resolveDisplayName(user),
      description: user.attributes.bio,
      phone: user.attributes.phone,
      roleId: user.attributes.primary_role,
      profilePhoto: profilePhoto?.url ?? null,
      latitude: user.attributes.latitude ? Number(user.attributes.latitude) : null,
      longitude: user.attributes.longitude ? Number(user.attributes.longitude) : null,
      website: user.attributes.website,
      eventCount: user.attributes.event_count,
      isFollowed: user.attributes.is_followed,
      currentFollowId: user.attributes.current_follow_id,
      followerCount: user.attributes.follower_count,
      likesCount: user.attributes.likes_count,
      isLiked: user.attributes.is_liked,
      currentLikeId: user.attributes.current_like_id,
      galleryImages: user.attributes.gallery_images ?? [],
      externalLinks: user.attributes.external_links ?? [],
      upcomingEvents,
      pastEvents,
    };
  }

  private resolveDisplayName(user: User): string {
    return user.attributes.display_name || '';
  }

  private async toProfileEventSummary(
    event: User['attributes']['upcoming_events'][number],
  ): Promise<PublicProfileEventSummary> {
    const fallbackPhoto = await this.venuePhotos.resolve(
      event.fallback_image_url,
      event.fallback_google_place_id,
    );
    return {
      id: event.id,
      publicId: event.public_id,
      title: event.title,
      image: event.poster_url,
      fallbackImage: fallbackPhoto?.url ?? null,
      date: event.date,
      time: event.time,
      likesCount: event.likes_count,
      isLiked: event.is_liked,
      currentLikeId: event.current_like_id,
      isPast: event.is_past,
    };
  }
}
